import React, { useState, useMemo, useCallback, useRef, memo } from 'react';
import {
  Box,
  Icon,
  Typography,
  Link,
  IconButton,
  ButtonBase,
  Tooltip,
  Menu,
  MenuItem,
} from '@material-ui/core';
import moment from 'moment';

import RatingChart from '../../../../components/RatingChart';
import { useAppointments } from '../../context';
import { useAppointment } from '../context';
import RatingComment from './RatingComment';
import useStyles from './styles';

const AppointmentItem = props => {
  const {
    id,
    date,
    startTime,
    endTime,
    userAbsent,
    userInfoAbsence,
    removed,
    professionalId,
    professionalName,
    skypeConsultant,
    rating,
    comments: ratingComment,
    status,
  } = props;

  const classes = useStyles();
  const { getStatusStyle } = useAppointments();
  const { getAppointmentActions, handleShowRatingModal } = useAppointment();
  const ratingChartRef = useRef(null);
  const menuAnchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const statusType = useMemo(() => {
    if (removed) return 'CANCELED';
    if (userAbsent) return 'ABSENT';
    if (userInfoAbsence) return 'INFO_ABSENCE';
    if (status === 'HAPPENED') return 'CONCLUDED';
    return 'SCHEDULED';
  }, [removed, status, userAbsent, userInfoAbsence]);

  const appointmentDate = useMemo(() => moment(date).format('DD/MM/YYYY'), [
    date,
  ]);

  const handleActionClick = useCallback(
    actionCb => {
      if (actionCb) actionCb();
      handleCloseMenu();
    },
    [handleCloseMenu],
  );

  const statusStyle = useMemo(() => getStatusStyle(statusType), [
    getStatusStyle,
    statusType,
  ]);

  const appointmentActions = useMemo(
    () =>
      getAppointmentActions(
        id,
        skypeConsultant,
        statusType,
        date,
        professionalId,
      ),
    [
      getAppointmentActions,
      id,
      skypeConsultant,
      statusType,
      date,
      professionalId,
    ],
  );

  return (
    <Box className={classes.container}>
      <Box className={`appointment-icon ${statusStyle.className}`}>
        <Icon>{statusStyle.icon}</Icon>
      </Box>
      <Box className={`appointment-infos-container ${statusStyle.className}`}>
        <Box className="appointment-date">
          <Typography variant="body2">{appointmentDate}</Typography>
          <Typography variant="caption">{`${startTime} às ${endTime}`}</Typography>
        </Box>
        <Box className="appointment-professional">
          <Typography
            className="appointment-professional-label"
            variant="caption"
          >
            Profissional:
          </Typography>
          <Typography className="appointment-professional-name" variant="body2">
            {professionalName}
          </Typography>
        </Box>
        <Box className="appointment-status">
          <Typography
            className={`appointment-status-label ${statusStyle.className}`}
            variant="caption"
          >
            {statusStyle.label}
            {statusType === 'INFO_ABSENCE' && (
              <Tooltip title="Você não cancelou o agendamento com 48h de antecedência mas informou que não poderá comparecer.">
                <Icon className="appointment-status-label-icon">info</Icon>
              </Tooltip>
            )}
            {statusType === 'ABSENT' && (
              <Tooltip title="O profissional informou que você não compareceu.">
                <Icon className="appointment-status-label-icon">info</Icon>
              </Tooltip>
            )}
          </Typography>
          {statusType === 'SCHEDULED' && (
            <Link
              className="appointment-status-link"
              underline="always"
              href={skypeConsultant}
              target="_blank"
            >
              <Typography variant="caption">Ir para o link</Typography>
            </Link>
          )}
          {statusType === 'CONCLUDED' && (
            <>
              {rating > 0 ? (
                <>
                  <ButtonBase
                    className="appointment-status-rating"
                    disableRipple
                    onClick={() => handleShowRatingModal(id)}
                    ref={ratingChartRef}
                  >
                    <RatingChart rate={rating} size="small" />
                  </ButtonBase>
                  <RatingComment
                    anchor={ratingChartRef}
                    comment={ratingComment}
                  />
                </>
              ) : (
                <Link
                  className="appointment-status-btn-rating"
                  underline="always"
                  onClick={() => handleShowRatingModal(id)}
                >
                  <Typography variant="caption">Avaliar agendamento</Typography>
                </Link>
              )}
            </>
          )}
        </Box>
      </Box>
      <Box className="appointment-actions">
        <IconButton
          className="appointment-actions-btn"
          ref={menuAnchorRef}
          onClick={handleOpenMenu}
        >
          <Icon color="inherit">more_horiz</Icon>
        </IconButton>
        <Menu
          className={classes.actionsMenu}
          anchorEl={menuAnchorRef.current}
          open={menuOpen}
          onClose={handleCloseMenu}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {appointmentActions.map(item => (
            <MenuItem
              key={item.title}
              onClick={() => handleActionClick(item.action)}
              disabled={item.disabled}
              className="actions-menu-item"
              {...item.props}
            >
              <Icon fontSize="small" className="actions-menu-item-icon">
                {item.icon}
              </Icon>
              <Typography variant="body2" className="actions-menu-item-text">
                {item.title}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default memo(AppointmentItem);
